import React from 'react';
import { Project, Task, Participant } from '../types';
import { FolderIcon, UsersIcon, CheckIcon } from './icons';
import './participant/screens/molecules.css';

interface ProjectChooserProps {
  projects: Project[];
  tasks: Task[];
  participants: Participant[];
  onSelect: (projectId: string) => void;
  onCreateProject: () => void;
  onLogout: () => void;
  userName?: string;
}

const ProjectChooser: React.FC<ProjectChooserProps> = ({
  projects,
  tasks,
  participants,
  onSelect,
  onCreateProject,
  onLogout,
  userName,
}) => {
  return (
    <div className="min-h-screen bg-app-bg flex flex-col relative overflow-hidden">
      {/* Molecular decorations — ember nodes + ink squiggles on light bg */}

      {/* Top-left — 4-node triangular cluster */}
      <div className="mol" style={{ position: 'absolute', top: 30, left: 20, pointerEvents: 'none', opacity: 0.5 }}>
        <svg width="110" height="95" viewBox="-10 -10 120 100" fill="none">
          <circle cx="0" cy="0" r="5" fill="#D4715A"/>
          <circle cx="40" cy="-12" r="3.5" fill="#D4715A"/>
          <circle cx="55" cy="22" r="4" fill="#D4715A"/>
          <circle cx="15" cy="40" r="3" fill="#D4715A"/>
          <line x1="4" y1="1" x2="37" y2="-10" stroke="#D4715A" strokeWidth="1.5" opacity="0.5"/>
          <line x1="43" y1="-9" x2="52" y2="18" stroke="#D4715A" strokeWidth="1.5" opacity="0.5"/>
          <line x1="3" y1="4" x2="13" y2="37" stroke="#D4715A" strokeWidth="1.5" opacity="0.5"/>
          <path d="M60,26 Q68,18 76,26 Q84,18 92,26" fill="none" stroke="#1a1814" strokeWidth="2" strokeLinecap="round" opacity="0.2"/>
        </svg>
      </div>

      {/* Top-right — 2-node vertical */}
      <div className="mol-d2" style={{ position: 'absolute', top: 50, right: 25, pointerEvents: 'none', opacity: 0.45 }}>
        <svg width="55" height="65" viewBox="-10 -10 65 75" fill="none">
          <circle cx="10" cy="0" r="4" fill="#D4715A"/>
          <circle cx="10" cy="35" r="3.5" fill="#D4715A"/>
          <line x1="10" y1="4" x2="10" y2="31" stroke="#D4715A" strokeWidth="1.5" opacity="0.5"/>
          <path d="M15,-4 Q23,-12 31,-4 Q39,-12 47,-4" fill="none" stroke="#1a1814" strokeWidth="2" strokeLinecap="round" opacity="0.2"/>
          <path d="M15,39 Q23,47 31,39 Q39,47 47,39" fill="none" stroke="#1a1814" strokeWidth="2" strokeLinecap="round" opacity="0.15"/>
        </svg>
      </div>

      {/* Mid-left — single node with wiggle */}
      <div className="mol-d4" style={{ position: 'absolute', top: '45%', left: 15, pointerEvents: 'none', opacity: 0.4 }}>
        <svg width="60" height="20" viewBox="-5 -10 65 20" fill="none">
          <circle cx="0" cy="0" r="3.5" fill="#D4715A"/>
          <path d="M5,0 Q13,-7 21,0 Q29,-7 37,0 Q45,-7 53,0" fill="none" stroke="#1a1814" strokeWidth="2" strokeLinecap="round" opacity="0.2"/>
        </svg>
      </div>

      {/* Mid-right — diagonal pair */}
      <div className="mol-d5" style={{ position: 'absolute', top: '50%', right: 20, pointerEvents: 'none', opacity: 0.4 }}>
        <svg width="55" height="40" viewBox="-5 -5 60 45" fill="none">
          <circle cx="0" cy="0" r="3" fill="#D4715A"/>
          <circle cx="30" cy="22" r="3.5" fill="#D4715A"/>
          <line x1="3" y1="2" x2="27" y2="20" stroke="#D4715A" strokeWidth="1.5" opacity="0.5"/>
          <path d="M34,25 Q42,33 50,25 Q58,33 66,25" fill="none" stroke="#1a1814" strokeWidth="2" strokeLinecap="round" opacity="0.18"/>
        </svg>
      </div>

      {/* Bottom-left — L-shape 3-node */}
      <div className="mol-d7" style={{ position: 'absolute', bottom: 50, left: 30, pointerEvents: 'none', opacity: 0.4 }}>
        <svg width="70" height="55" viewBox="-10 -5 75 55" fill="none">
          <circle cx="0" cy="0" r="3" fill="#D4715A"/>
          <circle cx="0" cy="28" r="3.5" fill="#D4715A"/>
          <circle cx="30" cy="28" r="3" fill="#D4715A"/>
          <line x1="0" y1="3" x2="0" y2="25" stroke="#D4715A" strokeWidth="1.5" opacity="0.5"/>
          <line x1="3" y1="28" x2="27" y2="28" stroke="#D4715A" strokeWidth="1.5" opacity="0.5"/>
          <path d="M-5,-4 Q-13,-12 -21,-4 Q-29,-12 -37,-4" fill="none" stroke="#1a1814" strokeWidth="2" strokeLinecap="round" opacity="0.18"/>
        </svg>
      </div>

      {/* Bottom-right — 3-node zigzag */}
      <div className="mol-d9" style={{ position: 'absolute', bottom: 40, right: 30, pointerEvents: 'none', opacity: 0.4 }}>
        <svg width="65" height="55" viewBox="-5 -5 70 55" fill="none">
          <circle cx="0" cy="0" r="3" fill="#D4715A"/>
          <circle cx="25" cy="20" r="3.5" fill="#D4715A"/>
          <circle cx="50" cy="40" r="3" fill="#D4715A"/>
          <line x1="3" y1="2" x2="22" y2="18" stroke="#D4715A" strokeWidth="1.5" opacity="0.5"/>
          <line x1="28" y1="22" x2="47" y2="38" stroke="#D4715A" strokeWidth="1.5" opacity="0.5"/>
          <path d="M54,43 Q62,51 70,43 Q78,51 86,43" fill="none" stroke="#1a1814" strokeWidth="2" strokeLinecap="round" opacity="0.18"/>
        </svg>
      </div>

      {/* Content — above decorations */}
      <div className="relative z-10 flex flex-col flex-1">
        {/* Header */}
        <div className="px-6 pt-10 pb-6">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-text-muted text-sm font-medium">
                {userName ? `Welcome back, ${userName}` : 'Welcome back'}
              </p>
              <h1 className="text-2xl font-extrabold text-text-ink mt-1">
                Choose a project
              </h1>
            </div>
            <button
              onClick={onLogout}
              className="text-xs font-medium text-text-muted border border-app-border rounded-lg px-3 py-1.5 hover:bg-white hover:text-text-ink transition-colors active:scale-95 mt-1"
            >
              Log out
            </button>
          </div>
        </div>

        {/* Project cards */}
        <div className="flex-1 px-5 pb-8">
          <div className="grid gap-3">
            {projects.map(project => {
              const projectTasks = tasks.filter(t => t.projectId === project.id);
              const activeTasks = projectTasks.filter(t => t.status === 'active');
              const projectParticipants = participants.filter(p => p.projectId === project.id);

              return (
                <button
                  key={project.id}
                  onClick={() => onSelect(project.id)}
                  className="bg-white rounded-2xl p-5 text-left border border-app-border hover:border-action/40 hover:shadow-soft transition-all active:scale-[0.98]"
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className="w-11 h-11 rounded-xl bg-action/10 flex items-center justify-center flex-shrink-0">
                      <FolderIcon className="w-5 h-5 text-action" />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-[15px] font-bold text-text-ink truncate">
                        {project.name}
                      </h3>
                      {project.description && (
                        <p className="text-xs text-text-muted mt-0.5 line-clamp-1">
                          {project.description}
                        </p>
                      )}

                      {/* Stats row */}
                      <div className="flex items-center gap-4 mt-3">
                        <div className="flex items-center gap-1.5">
                          <CheckIcon className="w-3.5 h-3.5 text-text-faint" />
                          <span className="text-xs text-text-muted font-medium">
                            {activeTasks.length} task{activeTasks.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <UsersIcon className="w-3.5 h-3.5 text-text-faint" />
                          <span className="text-xs text-text-muted font-medium">
                            {projectParticipants.length} participant{projectParticipants.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}

            {/* Create project card */}
            <button
              onClick={onCreateProject}
              className="rounded-2xl p-5 text-left border-2 border-dashed border-app-border hover:border-action/40 transition-colors active:scale-[0.98]"
            >
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-app-bg flex items-center justify-center flex-shrink-0">
                  <span className="text-xl text-text-faint font-light">+</span>
                </div>
                <div>
                  <h3 className="text-[15px] font-bold text-text-muted">Create new project</h3>
                  <p className="text-xs text-text-faint mt-0.5">Start a new research study</p>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectChooser;
